import type { ReactNode, MouseEvent } from 'react';
import type { Dayjs } from 'dayjs';
import { DialogContentText, FormGroup, Stack } from '@mui/material';
import { useTranslation } from '../../../contexts/TranslationContext';
import { MHMDatePicker } from '../MHM/MHMDatePicker';
import {
  PickersCalendarHeader,
  type PickersCalendarHeaderProps,
} from '@mui/x-date-pickers';
import { MHMButton } from '../MHM/MHMButton';

type DownloadCSVModalDatesProps = {
  minStartDate: Date;
  maxEndDate: Date;
  startDate: Date;
  setStartDate: (date: Date) => void;
  endDate: Date;
  setEndDate: (date: Date) => void;
};

type HeaderProps = {
  children?: ReactNode;
  onClick?: (e: MouseEvent) => void;
};

function makeHeader({ children, onClick }: HeaderProps) {
  return function Header(pickerProps: PickersCalendarHeaderProps<Dayjs>) {
    return (
      <Stack direction="row">
        <PickersCalendarHeader {...pickerProps} />
        <MHMButton
          size="small"
          sx={{ m: '12px 24px 4px 0', fontSize: '16px', lineHeight: 1.25 }}
          onClick={onClick}
        >
          {children}
        </MHMButton>
      </Stack>
    );
  };
}

export function DownloadCSVModalDates({
  minStartDate,
  maxEndDate,
  startDate,
  setStartDate,
  endDate,
  setEndDate,
}: DownloadCSVModalDatesProps) {
  const { t } = useTranslation();

  const isStartDateValid =
    startDate >= minStartDate &&
    startDate <= maxEndDate &&
    startDate <= endDate;

  const isEndDateValid =
    endDate >= minStartDate && endDate <= maxEndDate && endDate >= startDate;

  const StartDateHeader = makeHeader({
    children: t('Earliest'),
    onClick: e => {
      e.stopPropagation();
      setStartDate(minStartDate);
    },
  });

  const EndDateHeader = makeHeader({
    children: t('Latest'),
    onClick: e => {
      e.stopPropagation();
      setEndDate(maxEndDate);
    },
  });

  return (
    <>
      <FormGroup>
        <DialogContentText>{t('Start Date')}</DialogContentText>
        <MHMDatePicker
          value={startDate}
          shouldDisableDate={date => date < minStartDate || date > maxEndDate}
          onChange={date => setStartDate(date)}
          error={!isStartDateValid}
          slots={{ calendarHeader: StartDateHeader }}
          slotProps={{ popper: { placement: 'right' } }}
          data-testid="download-start-date"
        />
      </FormGroup>
      <FormGroup>
        <DialogContentText>{t('End Date')}</DialogContentText>
        <MHMDatePicker
          value={endDate}
          shouldDisableDate={date => date > maxEndDate || date < minStartDate}
          onChange={date => setEndDate(date)}
          error={!isEndDateValid}
          slots={{ calendarHeader: EndDateHeader }}
          slotProps={{ popper: { placement: 'right' } }}
          data-testid="download-end-date"
        />
      </FormGroup>
    </>
  );
}
